import React from 'react';
import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import Button from '@components/atoms/button/button';
import { ModalContext } from '@context/modal.context';
import { SavedPaymentCard } from '@interfaces/models/paymentMethod';
import useDeletePaymentCard from '@hooks/account-payments/use-delete-payment-card';
import useUser from '@hooks/user/use-user';
import styles from './delete-payment-card-confirmation.module.scss';

type DeletePaymentCardConfirmationProps = {
  cardId: SavedPaymentCard['id'];
  lastFourDigits: string;
};

const DeletePaymentCardConfirmation: React.FC<DeletePaymentCardConfirmationProps> = ({ cardId, lastFourDigits }) => {
  const { t } = useTranslation('account-settings');
  const closeModal = useContextSelector(ModalContext, (v) => v.closeModal);
  const { user } = useUser();
  const { mutate: deletePaymentCard, isLoading } = useDeletePaymentCard(user?.id);

  return (
    <div className={styles.deletePaymentCardConfirmation}>
      <h2 className={styles.deletePaymentCardConfirmation__title}>
        {t('ACCOUNT_SETTINGS.PAYMENT_METHODS.DELETE_TITLE')}
      </h2>
      <p className={styles.deletePaymentCardConfirmation__description}>
        {t('ACCOUNT_SETTINGS.PAYMENT_METHODS.DELETE_TEXT').replace('%@', lastFourDigits)}
      </p>
      <div className={styles.deletePaymentCardConfirmation__actions}>
        <Button
          variant="secondary"
          size="large"
          onClick={closeModal}
          disabled={isLoading}
          data-cy="delete-payment-card-cancel"
        >
          {t('ACCOUNT_SETTINGS.PAYMENT_METHODS.DELETE_CANCEL_CTA')}
        </Button>
        <Button
          variant="primary"
          size="large"
          onClick={() => deletePaymentCard(cardId, { onSuccess: closeModal })}
          disabled={isLoading}
          data-cy="delete-payment-card-confirm"
        >
          {t('ACCOUNT_SETTINGS.PAYMENT_METHODS.DELETE_CTA')}
        </Button>
      </div>
    </div>
  );
};

export default DeletePaymentCardConfirmation;
